import { useMutation } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from './base';
import { HTTPInstance } from '../core';
import {
  CreateFamilyTreePayload,
  FamilyTree,
  Person,
  AddMemberPayload,
  DeleteMemberPayload,
  AddStoryPayload,
  FamilyStory,
  UpdateStoryPayload,
  UpdateTreePayload,
  TreeValidationResult,
  TreeRepairResult,
} from '../types/tree';

export const useGetFamilyTreesByUser = (
  userId: string,
  collaborator?: string,
) => {
  return useApiQuery<FamilyTree[]>(
    ['familyTrees', userId],
    `/trees/${userId}/user${collaborator ? `?collaborator=${collaborator}` : null}`,
    {
      queryKey: ['familyTrees', userId],
      enabled: !!userId,
    },
  );
};

export const useGetFamilyTree = (treeId: string, enabled: boolean = true) => {
  return useApiQuery<FamilyTree>(
    ['familyTrees', treeId],
    `/trees/${treeId}/tree`,
    {
      queryKey: ['familyTrees', treeId],
      enabled: !!treeId && enabled,
    },
  );
};

export const useCreateFamilyTree = () => {
  return useApiMutation<FamilyTree, CreateFamilyTreePayload>('/trees', 'POST');
};

export const useUpdateMember = (personId: string) => {
  return useApiMutation<Person, FormData>(`/members/${personId}`, 'PUT');
};

export const useAddMemberFamilyTree = (treeId: string) => {
  return useApiMutation<FamilyTree, AddMemberPayload>(
    `/add-member-tree/${treeId}`,
    'POST',
  );
};

export const useDeleteFamilyTree = (treeId: string) => {
  return useApiMutation<void, void>(`/trees/${treeId}`, 'DELETE');
};

export const useDeleteFamilyTreeMember = (treeId: string) => {
  return useApiMutation<null, DeleteMemberPayload>(
    `/trees/${treeId}/member`,
    'DELETE',
  );
};

export const useAddCollaborators = (treeId: string) => {
  return useApiMutation<FamilyTree, any>(`/add-collaborators/${treeId}`, 'PUT');
};

export const useRemoveSelfFromCollaborations = () => {
  return useApiMutation<void, void>('/collaborator/remove-self', 'DELETE');
};

export const useUpdateTree = (treeId: string) => {
  return useApiMutation<FamilyTree, UpdateTreePayload>(
    `/trees/${treeId}`,
    'PUT',
  );
};

export const useGetFamilyStories = (treeId: string) => {
  return useApiQuery<FamilyStory[]>(
    ['familyStories', treeId],
    `/stories/${treeId}`,
    {
      queryKey: ['familyStories', treeId],
      enabled: !!treeId,
    },
  );
};

export const useGetFamilyStoriesById = (storyId: string) => {
  return useApiQuery<FamilyStory>(
    ['familyStories', storyId],
    `/stories/${storyId}/story`,
    {
      queryKey: ['familyStories', storyId],
      enabled: !!storyId,
    },
  );
};

export const useCreateStory = () => {
  return useApiMutation<FamilyStory, AddStoryPayload | FormData>(
    '/stories',
    'POST',
  );
};

export const useUpdateStory = (storyId: string) => {
  return useApiMutation<FamilyStory, UpdateStoryPayload | FormData>(
    `/stories/${storyId}`,
    'PUT',
  );
};

export const useDeleteFamilyStory = (storyId: string) => {
  return useApiMutation<void, void>(`/stories/${storyId}`, 'DELETE');
};

export const useImportGedcom = () => {
  return useApiMutation<FamilyTree, FormData>('/gedcom/import', 'POST');
};

export const useExportGedcom = () => {
  return useMutation({
    mutationFn: async (treeId: string) => {
      const response = await HTTPInstance.get(`/gedcom/export/${treeId}`, {
        responseType: 'blob',
      });
      return response;
    },
  });
};

export const useValidateTree = () => {
  return useMutation({
    mutationFn: async (treeId: string) => {
      const response = await HTTPInstance.post<TreeValidationResult>(
        `/trees/${treeId}/validate-tree`,
      );
      return response.data;
    },
  });
};

export const useRepairTree = () => {
  return useMutation({
    mutationFn: async (treeId: string) => {
      const response = await HTTPInstance.post<TreeRepairResult>(
        `/trees/${treeId}/repair-tree`,
      );
      return response.data;
    },
  });
};
